import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

/**
 * 预定义主题色方案
 * main 为空格分隔的 RGB 值，供 css `rgb(var(--theme-50))` 使用
 * 所有渐变 / 浅色 / 透明效果均通过 `rgb(var(--theme-50) / 0.x)` 实现
 */
export const COLOR_THEMES = {
  purple:  { main: '124 58 237',  name: '紫色' },
  blue:    { main: '59 130 246',  name: '蓝色' },
  indigo:  { main: '99 102 241',  name: '靛蓝' },
  green:   { main: '16 185 129',  name: '绿色' },
  teal:    { main: '20 184 166',  name: '青色' },
  rose:    { main: '244 63 94',   name: '玫红' },
  orange:  { main: '249 115 22',  name: '橙色' },
  amber:   { main: '180 130 56',  name: '琥珀' },
  brown:   { main: '141 98 75',   name: '棕色' },
  beige:   { main: '162 138 100', name: '米色' },
  slate:   { main: '100 116 139', name: '石墨' },
  crimson: { main: '190 49 68',   name: '绯红' },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // 从localStorage获取保存的主题，默认为light
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const saved = localStorage.getItem('colorTheme');
    return saved && COLOR_THEMES[saved] ? saved : 'purple';
  });

  /** 将主题色写入 CSS 变量 */
  const applyColorTheme = useCallback((colorKey) => {
    const colors = COLOR_THEMES[colorKey] || COLOR_THEMES.purple;
    const root = document.documentElement;
    root.style.setProperty('--theme-50', colors.main);
  }, []);

  useEffect(() => {
    // 应用主题到document
    document.documentElement.setAttribute('data-theme', theme);
    // 保存到localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    applyColorTheme(colorTheme);
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme, applyColorTheme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    colorTheme,
    setColorTheme,
    colorThemes: COLOR_THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
